import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import {
  CardPin,
  CheckDouble as CheckDoubleIcon,
  Cross as CrossIcon,
} from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  BulkRejectApprovalsv2ReviewRequestDocument,
  InboxTaskListItemCompletionFragmentDoc,
  InboxTasksReviewerDocument,
  TaskActivityDocument,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import {
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'
import { getExpensesExpensableMetadata } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable'
import { getProcurementPurchaseRequestMetadata } from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'
import { RESOURCE_COMPONENTS, RESOURCE_TITLES } from 'modules/inbox_tasks/tasks/approval/helpers'
import ReasonInput from 'modules/inbox_tasks/tasks/approvalsv2/ReasonInput'

type State = {
  additionalComments?: string
}

const isV2ResourceType = (
  typename: string
): typename is 'ProcurementPurchaseRequest' | 'ExpensesExpensable' => {
  return typename === 'ProcurementPurchaseRequest' || typename === 'ExpensesExpensable'
}

const formatAlertDate = (timestamp: string) => {
  const date = stringToDate(timestamp, 'backend_datetime')
  return i18nDateToString(date, 'ui_short_with_time')
}

const ApprovalsV2TaskContent: TaskCustomComponent<'ApprovalsV2ReviewRequest', State> = ({
  affectee,
  ...props
}) => {
  const { employee, access } = useCurrentSession()

  const taskable = 'taskable' in props ? props.taskable : undefined

  const approvalProcess = taskable?.approvalProcess

  const approvable = approvalProcess?.resource

  const resourceId = approvable && 'id' in approvable ? approvable.id : undefined

  const reason = approvalProcess?.decisionReason
  const isRejected = approvalProcess?.decision === 'rejected'
  const resourceTypename = approvable?.__typename ?? ''

  const owner = approvalProcess?.resourceOwner?.employeeName
  const ownerId = owner?.id

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  const reviewerTime = taskable?.reviewedAt || ''
  const reviewerId = taskable?.reviewedById

  const isCurrentUserApprover = reviewerId === employee.id

  const { data: employeeData } = useQuery(InboxTasksReviewerDocument, {
    variables: { employeeId: reviewerId || 0 },
    skip: !reviewerId || isCurrentUserApprover,
  })

  const reviewerName =
    employeeData?.employees?.employee?.employeeName?.fullName ||
    (isCurrentUserApprover ? access.fullName : '')

  const Content = useUnsafeMemo(() => {
    if (!resourceId || !resourceTypename) {
      return null
    }

    const Component = RESOURCE_COMPONENTS[resourceTypename]

    if (!Component) return null

    return (
      <Component
        id={resourceId}
        requesterContent={requesterContent}
        affectee={affectee}
        requesterEmployeeId={ownerId}
      />
    )
  }, [affectee, resourceTypename, resourceId, requesterContent, ownerId])

  return (
    <Box gap="s24" overflowX="hidden" flexGrow>
      {isRejected && !!reason && (
        <OneAlert
          variant="critical"
          title={i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
            reason: reason,
          })}
          description={
            reviewerName && reviewerTime
              ? `${reviewerName}, ${formatAlertDate(reviewerTime)}`
              : reviewerTime
              ? formatAlertDate(reviewerTime)
              : ''
          }
          icon={CardPin}
        />
      )}
      {Content}
    </Box>
  )
}

const ApprovalsV2TaskFooter: TaskFooterComponent<'ApprovalsV2ReviewRequest', State> = ({
  taskId,
  taskable,
  status,
}) => {
  const apolloClient = useApolloClient()

  const { company, employee } = useCurrentSession()

  const [isRejectExpanded, setIsRejectExpanded] = React.useState(false)

  const [bulkApprove] = useMutation(BulkApproveApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
  })

  const [bulkReject] = useMutation(BulkRejectApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRejectApprovalsv2ReviewRequest,
  })

  const handleSuccessApproveReject = useUnsafeCallback(() => {
    apolloClient.writeFragment({
      id: `TasksInboxTask:${taskId}`,
      fragment: InboxTaskListItemCompletionFragmentDoc,
      fragmentName: 'InboxTaskListItemCompletion',
      data: {
        __typename: 'TasksInboxTask',
        id: taskId,
        completedAt: new Date().toISOString(),
        status: 'done',
      },
    })

    apolloClient.refetchQueries({
      include: [TaskActivityDocument],
    })
  }, [apolloClient, taskId])

  const handleApprove = useUnsafeCallback(async () => {
    if (!taskable?.id) return

    const result = await bulkApprove({
      variables: {
        companyId: company.id,
        reviewRequestIds: [taskable.id],
        reviewerId: employee.id,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('settings.approvals.approval_action.flash_messages.approve'),
      })
      handleSuccessApproveReject()
    }

    return handled.success
  }, [taskable?.id, company.id, employee.id, bulkApprove, handleSuccessApproveReject])

  const handleReject = useUnsafeCallback(
    async (reason?: string) => {
      if (!taskable?.id) return

      const result = await bulkReject({
        variables: {
          companyId: company.id,
          reviewRequestIds: [taskable.id],
          reviewerId: employee.id,
          reason: reason,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.approvals.approval_action.flash_messages.reject'),
        })
        handleSuccessApproveReject()
      }

      return handled.success
    },
    [taskable?.id, company.id, employee.id, bulkReject, handleSuccessApproveReject]
  )

  if (!taskable?.id || status === 'done') {
    return null
  }

  return (
    <Box flexDirection="row" gap="s12" justifyContent="flexEnd" flexGrow>
      {!isRejectExpanded && (
        <F0Button
          label={i18n.t('common.reject')}
          variant="outline"
          icon={CrossIcon}
          onClick={() => setIsRejectExpanded(true)}
          tooltip={false}
        />
      )}
      <ReasonInput
        onAction={handleReject}
        isExpanded={isRejectExpanded}
        onCancel={() => setIsRejectExpanded(false)}
        textareaLabel={i18n.t('tasks.approvals.rejection_reason.label')}
        textareaPlaceholder={i18n.t('tasks.approvals.rejection_reason.placeholder')}
        confirmButtonLabel={i18n.t('tasks.approvals.rejection_reason.reject')}
      />
      {!isRejectExpanded && (
        <F0Button label={i18n.t('common.approve')} icon={CheckDoubleIcon} onClick={handleApprove} />
      )}
    </Box>
  )
}

export const ApprovalsV2TaskDefinition: TaskDefinition<'ApprovalsV2ReviewRequest', State> = {
  taskableTypename: 'ApprovalsV2ReviewRequest',
  initialState: {},
  component: ApprovalsV2TaskContent,
  footer: ApprovalsV2TaskFooter,
  getTitle: ({ info, affecteeName }) => {
    const approvableTypename = info?.approvalProcess?.resourceType

    if (!affecteeName || !approvableTypename) {
      return 'Review request'
    }

    const resourceTitleFunction = isV2ResourceType(approvableTypename)
      ? RESOURCE_TITLES[approvableTypename]
      : undefined

    const resource = resourceTitleFunction?.()
    const resourceText = typeof resource === 'string' ? resource : ''

    return i18n.t('common.approvals.title', {
      affectee: affecteeName,
      resource: resourceText,
    })
  },
  getMetadata: ({ info }) => {
    const approvable = info.approvalProcess?.resource

    if (!approvable) return []

    switch (approvable.__typename) {
      case 'ProcurementPurchaseRequest':
        return getProcurementPurchaseRequestMetadata(approvable)
      case 'ExpensesExpensable':
        return getExpensesExpensableMetadata(approvable)
      default:
        return []
    }
  },
}
